"use client";

import type React from "react";
import { useEffect, useState } from "react";
import { useAppContext } from "../../../shared/functions/Context";
import type { RatingStatus } from "../../../shared/models/three-sixty-feedback-models/Committee";
import type { ITemplateRating } from "../../../shared/models/three-sixty-feedback-models/TemplateRating";
import {
  Clock,
  CheckCircle2,
  Play,
  Activity,
  FileText,
  Calendar,
  Building,
  Briefcase,
} from "lucide-react";
import { dateFormat_YY_MM_DY } from "../../shared/utils/utils";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";

// Enhanced RequestCard component with better Tailwind styling
const RequestCard = observer(
  ({ assignment }: { assignment: ITemplateRating }) => {
    const navigate = useNavigate();
    const { store } = useAppContext();
    const users = store.user.all.map((user) => user.asJson);

    const getUserName = (userId: string): string => {
      const user = users.find((user) => user.uid === userId);
      return user?.displayName ?? "";
    };

    const getUserData = (userId: string) => {
      const user = users.find((user) => user.uid === userId);
      return user;
    };

    const getDepartmentName = (departmentId: string): string => {
      const department = store.department.all.find(
        (dept) => dept.asJson.id === departmentId
      );
      return department?.asJson.name ?? "Unknown Department";
    };

    const handleClick = (rateeId: string, sessionId: string) => {
      store.templateRating.select(assignment);
      navigate(`/c/questionnaire/competencies/${rateeId}/${sessionId}`);
    };

    const userData = getUserData(assignment.rateeId);
    const initials =
      userData?.displayName
        ?.split(" ")
        .map((name) => name[0])
        .join("")
        .toUpperCase() || "N/A";

    // Determine styling based on status
    let statusConfig = {
      borderColor: "border-slate-200",
      buttonColor: "bg-blue-600 hover:bg-blue-700",
      statusBadge: "bg-gray-100 text-gray-800",
      cardShadow: "shadow-sm",
    };

    switch (assignment.status) {
      case "In Progress":
        statusConfig = {
          borderColor: "border-amber-400",
          buttonColor: "bg-amber-600 hover:bg-amber-700",
          statusBadge: "bg-amber-100 text-amber-800",
          cardShadow: "shadow-md",
        };
        break;
      case "Completed":
        statusConfig = {
          borderColor: "border-green-400",
          buttonColor: "bg-green-600 hover:bg-green-700",
          statusBadge: "bg-green-100 text-green-800",
          cardShadow: "shadow-md",
        };
        break;
      case "Not Started":
        statusConfig = {
          borderColor: "border-red-400",
          buttonColor: "bg-red-600 hover:bg-red-700",
          statusBadge: "bg-red-100 text-red-800",
          cardShadow: "shadow-md",
        };
        break;
    }

    return (
      <div
        className={`bg-white rounded-xl ${statusConfig.cardShadow} ${statusConfig.borderColor} border-l-4 p-6 transition-all duration-300 hover:shadow-lg`}
      >
        {/* User Rating Header */}
        <div className="mb-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                {initials}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="mb-2">
                <p className="text-sm text-gray-600 mb-1">You are rating</p>
                <h4 className="text-lg font-bold text-gray-900 truncate">
                  {getUserName(assignment.rateeId)}
                </h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-gray-600">Department</p>
                    <p className="font-medium text-gray-900">
                      {getDepartmentName(userData?.department || "")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-gray-600">Position</p>
                    <p className="font-medium text-gray-900">
                      {userData?.jobTitle || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Request Details */}
        <div className="space-y-4 mb-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="text-sm font-semibold text-gray-700 mb-2">
              Description
            </h5>
            <p className="text-sm text-gray-900 bg-white rounded border px-3 py-2">
              {assignment.heading || "No description provided"}
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="text-sm font-semibold text-gray-700 mb-2">
              Reasons for Request
            </h5>
            <p className="text-sm text-gray-900 bg-white rounded border px-3 py-2">
              {assignment.reasonForRequest || "No reason provided"}
            </p>
          </div>
        </div>

        {/* Metadata */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <Calendar className="h-4 w-4" />
            <div>
              <span className="font-medium">Requested:</span>
              <span className="ml-1">
                {dateFormat_YY_MM_DY(assignment.dateRequested ?? 0)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <Clock className="h-4 w-4" />
            <div>
              <span className="font-medium">Due:</span>
              <span className="ml-1">
                {dateFormat_YY_MM_DY(assignment.dueDate)}
              </span>
            </div>
          </div>
        </div>

        {/* Status and Action */}
        <div className="flex items-center justify-between">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusConfig.statusBadge}`}
          >
            {assignment.status === "In Progress" && (
              <Play className="h-3 w-3 mr-1" />
            )}
            {assignment.status === "Completed" && (
              <CheckCircle2 className="h-3 w-3 mr-1" />
            )}
            {assignment.status === "Not Started" && (
              <Clock className="h-3 w-3 mr-1" />
            )}
            {assignment.status}
          </span>

          <button
            onClick={() =>
              handleClick(assignment.rateeId, assignment.templateId)
            }
            className={`px-4 py-2 rounded-lg text-white font-medium text-sm transition-colors duration-200 ${statusConfig.buttonColor} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
          >
            {assignment.status !== "Completed"
              ? "Start Rating"
              : "View Results"}
          </button>
        </div>
      </div>
    );
  }
);

const UserRequests = () => {
  const { store, api } = useAppContext();
  const me = store.auth.meJson;
  const assignments = store.templateRating?.all
    .filter((rate) => rate.asJson.raterId === me?.uid || "")
    .map((rate) => rate.asJson);
  const [loading, setLoading] = useState(false);

  const toDo = assignments.filter(
    (assignment) => assignment.status === "Not Started"
  );
  const inProgress = assignments.filter(
    (assignment) => assignment.status === ("In Progress" as RatingStatus)
  );
  const completed = assignments.filter(
    (assignment) => assignment.status === "Completed"
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await api.templateRating.getAll();
      } catch (error) {
        console.error("Error fetching data:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [api.templateRating, me?.uid, store.templateRating]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative mb-4">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 mx-auto"></div>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent absolute top-0 left-1/2 transform -translate-x-1/2"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Activity className="h-6 w-6 text-blue-600 animate-pulse" />
            </div>
          </div>
          <p className="text-gray-600 font-medium">Loading your requests...</p>
        </div>
      </div>
    );
  }

  const KanbanColumn = ({
    title,
    items,
    icon,
    color,
    bgColor,
    borderColor,
  }: {
    title: string;
    items: any[];
    icon: React.ReactNode;
    color: string;
    bgColor: string;
    borderColor: string;
  }) => (
    <div className="flex-1 min-w-80">
      {/* Column Header */}
      <div
        className={`${bgColor} rounded-xl p-6 mb-6 border-l-4 ${borderColor} shadow-sm`}
      >
        <div className="flex items-center gap-4">
          <div className={`p-3 ${color} rounded-xl shadow-sm`}>{icon}</div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">{title}</h3>
            <p className="text-sm text-gray-600 mt-1">
              {items.length} {items.length === 1 ? "item" : "items"}
            </p>
          </div>
        </div>
      </div>

      {/* Column Content */}
      <div className="space-y-6 max-h-[700px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        {items.length > 0 ? (
          items
            .sort(
              (a, b) =>
                new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime()
            )
            .map((assignment) => (
              <RequestCard key={assignment.id} assignment={assignment} />
            ))
        ) : (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-4 bg-gray-100 rounded-full w-fit mx-auto mb-4">
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              No {title.toLowerCase()} items
            </h4>
            <p className="text-gray-500">All caught up in this category!</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Full-width Header */}
      {/* <div className="mb-8">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl shadow-lg p-8 text-white w-full">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold">My Rating Requests</h1>
                <p className="text-blue-100 text-sm">
                  Manage your rating assignments and track progress
                </p>
              </div>
            </div>

          
            <div className="flex items-center gap-6 bg-white/10 rounded-xl p-4 backdrop-blur-sm">
              <div className="text-center">
                <div className="text-3xl font-bold text-white">
                  {toDo.length}
                </div>
                <div className="text-xs text-blue-100 font-medium">To Do</div>
              </div>
              <div className="w-px h-8 bg-white/20"></div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">
                  {inProgress.length}
                </div>
                <div className="text-xs text-blue-100 font-medium">
                  In Progress
                </div>
              </div>
              <div className="w-px h-8 bg-white/20"></div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">
                  {completed.length}
                </div>
                <div className="text-xs text-blue-100 font-medium">
                  Completed
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> */}

      {/* Full-width Kanban Board */}
      <div className="flex gap-8 overflow-x-auto pb-6 w-full">
        <KanbanColumn
          title="To Do"
          items={toDo}
          icon={<Clock className="h-6 w-6 text-amber-600" />}
          color="bg-amber-100"
          bgColor="bg-amber-50"
          borderColor="border-amber-400"
        />

        <KanbanColumn
          title="In Progress"
          items={inProgress}
          icon={<Play className="h-6 w-6 text-blue-600" />}
          color="bg-blue-100"
          bgColor="bg-blue-50"
          borderColor="border-blue-400"
        />

        <KanbanColumn
          title="Completed"
          items={completed}
          icon={<CheckCircle2 className="h-6 w-6 text-green-600" />}
          color="bg-green-100"
          bgColor="bg-green-50"
          borderColor="border-green-400"
        />
      </div>

      {/* Empty State */}
      {assignments.length === 0 && (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-200 w-full">
          <div className="p-6 bg-gray-100 rounded-full w-fit mx-auto mb-6">
            <FileText className="h-16 w-16 text-gray-400" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            No Requests Available
          </h3>
          <p className="text-gray-600 text-lg mb-6">
            You don't have any rating requests at the moment.
          </p>
          <div className="text-sm text-gray-500">
            Check back later or contact your administrator if you expect to have
            assignments.
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRequests;
